import { Tooltip, Typography } from 'antd'
import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'

import {
  formatByVariant,
  formatDateTime,
  toIsoString,
  type DateInput,
  type DateTimeVariant
} from 'src/utils'

const { Text } = Typography

type Props = {
  value: DateInput
  /** Format preset. Defaults to 'date' ("Mar 15, 2026"). */
  variant?: DateTimeVariant
  /** Override the i18n-derived locale. */
  locale?: string
  /** Custom timezone for the formatted value (browser local by default). */
  timeZone?: string
  /** Hide the hover-tooltip with the precise date/time. */
  noTooltip?: boolean
  /** What to render when value is null/invalid. Defaults to em-dash. */
  empty?: string
  /** Apply Typography.Text `type="secondary"`. */
  muted?: boolean
  /** Inline style override (e.g. set fontSize for table cells). */
  style?: CSSProperties
  className?: string
}

/**
 * Renders a localized date / date-time across the app. Pair with a table column
 * or anywhere a single timestamp value appears — keeps formatting consistent.
 *
 * The visible value is keyed off `variant`; the tooltip always shows the full
 * date+time so the precise moment is one hover away even for relative cells.
 */
export const DateTimeCell = ({
  value,
  variant = 'date',
  locale,
  timeZone,
  noTooltip,
  empty = '—',
  muted,
  style,
  className
}: Props) => {
  const { i18n } = useTranslation()
  const resolvedLocale = locale ?? i18n.language

  const display = formatByVariant(value, variant, {
    locale: resolvedLocale,
    timeZone
  })

  if (!display) {
    return (
      <Text type="secondary" style={style} className={className}>
        {empty}
      </Text>
    )
  }

  const iso = toIsoString(value)
  const tooltipText = formatDateTime(value, {
    locale: resolvedLocale,
    dateStyle: 'long',
    timeStyle: 'medium',
    timeZone
  })

  const content = (
    <Text
      type={muted ? 'secondary' : undefined}
      style={{ fontVariantNumeric: 'tabular-nums', ...style }}
      className={className}
    >
      <time dateTime={iso}>{display}</time>
    </Text>
  )

  if (noTooltip || variant === 'dateTime') {
    return content
  }

  return <Tooltip title={tooltipText}>{content}</Tooltip>
}
